import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Camera, MapPin, Calendar, User, Search,
  CheckCircle2, Clock, AlertTriangle, Send, Eye, Filter,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Shared listing for "Relatórios de Supervisão". Adapts to the user role:
 *   · admin     — full filters (status / date range / search) + photos count
 *   · employee  — only their own reports (auto-scoped by backend)
 *   · client    — only their approved/sent reports (auto-scoped by backend)
 *
 * Backend: GET /api/supervision-reports?status=&apartment_id=&date_from=&date_to=&q=
 * Detail page lives at `${roleBase}/relatorios/:id` (handled by each role app).
 */

const STATUS_META = {
  draft:     { label: "Rascunho",   color: "bg-[#1a4640] text-[#a0b2af] border-[#2a564f]", icon: FileText },
  submitted: { label: "Em revisão", color: "bg-[#3a2f0a] text-[#e8c860] border-[#d4af37]/40", icon: Clock },
  approved:  { label: "Aprovado",   color: "bg-[#0a3a33] text-[#7fd9c2] border-[#1a8868]", icon: CheckCircle2 },
  sent:      { label: "Enviado",    color: "bg-[#0a3a33] text-[#d4af37] border-[#d4af37]", icon: Send },
};

const FINAL_META = {
  ready:              { label: "Pronta p/ check-in", color: "text-emerald-400" },
  needs_maintenance:  { label: "Necessita manutenção", color: "text-amber-400" },
  needs_review:       { label: "Necessita revisão", color: "text-orange-400" },
  urgent_issue:       { label: "Problema urgente", color: "text-red-400" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${m.color}`}
          data-testid={`report-status-${status}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

export default function SupervisionReportsList({ basePath }) {
  // basePath e.g. "/admin/relatorios" or "/funcionario/relatorios" or "/cliente/relatorios"
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const role = user?.role || "client";
  const isAdmin = role === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [filters, setFilters] = useState({ status: "", date_from: "", date_to: "", q: "" });
  const [search, setSearch] = useState("");

  async function load() {
    if (!hasLoaded) setLoading(true);
    try {
      const params = {};
      if (isAdmin) {
        if (filters.status) params.status = filters.status;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
      }
      const { data } = await api.get("/supervision-reports", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao carregar relatórios"));
      setItems([]);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.date_from, filters.date_to]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(r =>
      (r.property?.name || "").toLowerCase().includes(q)
      || (r.property?.code || "").toLowerCase().includes(q)
      || (r.property?.address || "").toLowerCase().includes(q)
      || (r.property?.employee_name || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-6" data-testid="supervision-reports-list">
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#fdfbf7]">
            Relatórios de <span className="italic text-[#d4af37]">Supervisão</span>
          </h1>
          <p className="mt-1 text-sm text-[#a0b2af]">
            {isAdmin
              ? "Gestão e revisão dos relatórios pós-limpeza de toda a equipa."
              : role === "employee"
                ? "Os teus relatórios de limpeza submetidos."
                : "Relatórios de supervisão das suas propriedades."}
          </p>
        </div>
        <span className="text-xs uppercase tracking-[0.18em] text-[#a0b2af]"
              data-testid="reports-count">
          {filtered.length} {filtered.length === 1 ? "relatório" : "relatórios"}
        </span>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#1a4640] bg-[#062c27] p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0b2af]" />
            <input
              data-testid="reports-search"
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Pesquisar propriedade, código, funcionário…"
              className="w-full pl-9 pr-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] placeholder:text-[#a0b2af] focus:border-[#d4af37] focus:outline-none"
            />
          </div>

          {isAdmin && (
            <>
              <select
                data-testid="reports-filter-status"
                value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                className="px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
              >
                <option value="">Todos os estados</option>
                <option value="draft">Rascunho</option>
                <option value="submitted">Em revisão</option>
                <option value="approved">Aprovado</option>
                <option value="sent">Enviado</option>
              </select>
              <input
                data-testid="reports-filter-from"
                type="date"
                value={filters.date_from}
                onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                className="px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
              />
              <input
                data-testid="reports-filter-to"
                type="date"
                value={filters.date_to}
                onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                className="px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
              />
              {(filters.status || filters.date_from || filters.date_to) && (
                <button
                  data-testid="reports-clear-filters"
                  onClick={() => setFilters({ status: "", date_from: "", date_to: "", q: "" })}
                  className="px-3 py-2 rounded-md text-xs uppercase tracking-wider text-[#a0b2af] hover:text-[#d4af37] transition-colors"
                >
                  Limpar
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* List */}
      {loading && !hasLoaded ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-44 rounded-xl border border-[#1a4640] bg-[#062c27] animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#1a4640] bg-[#062c27] py-16 text-center"
             data-testid="reports-empty">
          <Filter size={32} className="mx-auto text-[#1a4640]" />
          <p className="mt-4 text-sm text-[#a0b2af]">
            {search || (isAdmin && (filters.status || filters.date_from || filters.date_to))
              ? "Nenhum relatório corresponde aos filtros aplicados."
              : isAdmin
                ? "Ainda não existem relatórios de supervisão. Aparecerão aqui à medida que a equipa os submeter."
                : role === "employee"
                  ? "Ainda não submeteu nenhum relatório de supervisão."
                  : "Os relatórios aparecerão aqui quando o admin os enviar após a limpeza."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(r => {
            const final = r.final_state ? FINAL_META[r.final_state] : null;
            return (
              <button
                key={r.id}
                data-testid={`report-card-${r.id}`}
                onClick={() => navigate(`${basePath}/${r.id}`)}
                className="group text-left rounded-xl border border-[#1a4640] bg-[#062c27] p-5 hover:border-[#d4af37]/60 hover:shadow-lg hover:shadow-[#d4af37]/10 hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-lg text-[#fdfbf7] truncate">
                      {r.property?.name || "Propriedade"}
                    </h3>
                    {r.property?.address && (
                      <p className="mt-1 text-xs text-[#a0b2af] flex items-center gap-1.5 truncate">
                        <MapPin size={12} className="shrink-0" />
                        <span className="truncate">{r.property.address}</span>
                      </p>
                    )}
                  </div>
                  <StatusBadge status={r.status} />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-[#a0b2af]">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={12} className="text-[#d4af37]" />
                    <span>{fmtDate(r.created_at)}</span>
                  </div>
                  {r.property?.employee_name && (
                    <div className="flex items-center gap-1.5 truncate">
                      <User size={12} className="text-[#d4af37]" />
                      <span className="truncate">{r.property.employee_name}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-[#1a4640]">
                  <div className="flex items-center gap-3 text-xs text-[#a0b2af]">
                    <span className="inline-flex items-center gap-1.5">
                      <Camera size={12} /> {r.photos_count || 0}
                    </span>
                    {r.occurrences?.length > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-amber-400">
                        <AlertTriangle size={12} /> {r.occurrences.length}
                      </span>
                    )}
                  </div>
                  {final ? (
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${final.color}`}>
                      {final.label}
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-[#1a4640] inline-flex items-center gap-1 group-hover:text-[#d4af37] transition-colors">
                      <Eye size={11} /> Ver detalhes
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
